/* eslint-disable promise/prefer-await-to-callbacks */
import type { FC } from 'react';
import type {
    IConnector,
    IPaginatedResponse,
    TPersona,
    TPersonaData
} from '@newo/api/gateway';
import { initMF } from '@/libs/initMf';
import type { VibeBuilderRemoteProps } from '@/modules/VibeMode/remote-entry.contract';

export const REMOTE_NAME = 'vibe-builder-remote';
export const REMOTE_MODULE = `${REMOTE_NAME}/VibeBuilder`;

export const VIBE_AGENT_INTEGRATION_IDN = 'newo_chat';
export const VIBE_AGENT_CONNECTOR_IDN = 'vibe_agent';
export const VIBE_AGENT_CLIENT_SECRET_SETTING_IDN = 'client_secret';

export const pickClientSecret = (
    connectors: IConnector[]
): string | undefined => {
    const connector = connectors.find(
        (c) => c.connector_idn === VIBE_AGENT_CONNECTOR_IDN
    );
    const setting = connector?.settings.find(
        (s) => s.idn === VIBE_AGENT_CLIENT_SECRET_SETTING_IDN
    );
    const value = setting?.value;
    return typeof value === 'string' && value.length > 0 ? value : undefined;
};

export type BuildVibePersonaNameParams = {
    email: string;
    customerIdn: string;
};

export const buildVibePersonaName = ({
    email,
    customerIdn
}: BuildVibePersonaNameParams): string => `vibe_${email}_${customerIdn}`;

export type TFindPersona = (
    name: string
) => Promise<IPaginatedResponse<TPersona>>;

export type TCreatePersona = (variables: {
    data: TPersonaData;
}) => Promise<unknown>;

export type ResolveVibePersonaParams = {
    personaName: string;
    findPersona: TFindPersona;
    createPersona: TCreatePersona;
};

const requestPersona = async ({
    personaName,
    findPersona,
    createPersona
}: ResolveVibePersonaParams): Promise<TPersona | undefined> => {
    const foundPersonas = await findPersona(personaName);
    const existingPersona = foundPersonas.items.at(0);

    if (existingPersona) {
        return existingPersona;
    }

    await createPersona({ data: { name: personaName } });

    const createdPersonas = await findPersona(personaName);

    return createdPersonas.items.at(0);
};

const personaRequests = new Map<string, Promise<TPersona | undefined>>();

export const resolveVibePersona = async (
    params: ResolveVibePersonaParams
): Promise<TPersona | undefined> => {
    const pendingRequest = personaRequests.get(params.personaName);

    if (pendingRequest) {
        return pendingRequest;
    }

    const request = requestPersona(params).catch((error: unknown) => {
        personaRequests.delete(params.personaName);
        throw error;
    });

    personaRequests.set(params.personaName, request);

    return request;
};

export type TVibeBuilderComponent = FC<VibeBuilderRemoteProps>;

const requestRemoteComponent = async (
    remoteSourceUrl: string
): Promise<TVibeBuilderComponent | undefined> => {
    const moduleFederation = await initMF({
        remoteSourceName: REMOTE_NAME,
        remoteSourceUrl
    });

    const remoteModule = await moduleFederation.loadRemote<{
        default: TVibeBuilderComponent;
    }>(REMOTE_MODULE);

    return remoteModule?.default;
};

let remoteComponentRequest:
    Promise<TVibeBuilderComponent | undefined> | undefined;

export const loadVibeBuilderComponent = async (
    remoteSourceUrl: string
): Promise<TVibeBuilderComponent | undefined> => {
    remoteComponentRequest ??= requestRemoteComponent(remoteSourceUrl).catch(
        (error: unknown) => {
            remoteComponentRequest = undefined;
            throw error;
        }
    );

    return remoteComponentRequest;
};
